/**
 * Adaptação de um campo de formulário para o esquema da linguagem de condição.
 *
 * Mesmo mapeamento do `FormularioEsquemaMapper` da T8a: NUMERO → NUMERO, TEXTO → TEXTO,
 * RADIO/SELECT → OPCAO, EDITOR/DATA → NAO_SUPORTADO. A ordem de preenchimento é `(ordem, id)`.
 */
import { CampoEsquema, Condicao, TipoCampoCondicao } from './tipos'

/** Campo do formulário na forma mínima de que o núcleo precisa (sem depender da view do módulo). */
export type CampoFormularioBase = {
  id?: number | string | null
  codigo?: string | null
  /** Valor do `InputType`: `NUMERO`, `TEXTO`, `RADIO`, `SELECT`, `EDITOR` ou `DATA`. */
  tipo: string
  multiplo?: boolean | null
  ordem: number
  opcoes?: Array<{ codigo?: string | null; descricao?: string | null }> | null
  condicaoExibicao?: Condicao | null
}

export const tipoCampoCondicaoDe = (tipo: string): TipoCampoCondicao => {
  switch (tipo) {
    case 'NUMERO':
      return 'NUMERO'
    case 'TEXTO':
      return 'TEXTO'
    case 'RADIO':
    case 'SELECT':
      return 'OPCAO'
    default:
      return 'NAO_SUPORTADO'
  }
}

/** `true` quando o campo pode ser referenciado por uma condição. */
export const campoReferenciavel = (campo: CampoFormularioBase): boolean =>
  tipoCampoCondicaoDe(campo.tipo) !== 'NAO_SUPORTADO' && !!campo.codigo

const desempateDe = (campo: CampoFormularioBase, indice: number): number => {
  const id = Number(campo.id)
  return Number.isFinite(id) && campo.id != null ? id : Number.MAX_SAFE_INTEGER - indice
}

/** Ordena os campos por `(ordem, id)`, como o backend. Campo sem id fica por último no empate. */
export const ordenarCampos = <T extends CampoFormularioBase>(campos: T[]): T[] =>
  campos
    .map((campo, indice) => ({ campo, indice }))
    .sort((a, b) =>
      a.campo.ordem !== b.campo.ordem
        ? a.campo.ordem - b.campo.ordem
        : desempateDe(a.campo, a.indice) - desempateDe(b.campo, b.indice)
    )
    .map(({ campo }) => campo)

export const campoEsquemaDe = (campo: CampoFormularioBase, indice: number): CampoEsquema => ({
  codigo: campo.codigo ?? '',
  tipo: tipoCampoCondicaoDe(campo.tipo),
  multiplo: !!campo.multiplo,
  ordem: campo.ordem,
  desempate: desempateDe(campo, indice),
  codigosOpcao: (campo.opcoes ?? [])
    .map((opcao) => opcao.codigo)
    .filter((codigo): codigo is string => !!codigo),
})

/** Esquema do formulário inteiro, só com os campos que têm código. */
export const esquemaDosCampos = (campos: CampoFormularioBase[]): CampoEsquema[] =>
  campos.map(campoEsquemaDe).filter((campo) => campo.codigo !== '')
